import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { RECENTLY_CONNECTED_THRESHOLD_MINUTES } from '@didaskill/domain/users/constants';
import dayjs from '@didaskill/services/dayjs';
import { AVATAR_CONNECTION_STATUS } from '@didaskill/ui/design-system';
import { globalMessages } from '@didaskill/ui/intl';

/**
 * Computes a user's activity status based on their last activity timestamp.
 *
 * This hook determines:
 * - The status (`ONLINE` or `OFFLINE`) depending on the time difference.
 * - An intl message indicating either recent connection or how long ago the user was active.
 * - A `recentlyConnected` flag if the user is considered recently active.
 *
 * @param lastActiveAt - ISO date string representing the user's last activity.
 *
 * @returns status - The computed activity status.
 * @returns message - Status message.
 * @returns recentlyConnected - Whether the user was recently connected.
 */
export const useProfileActiveStatus = (lastActiveAt: string) => {
  const intl = useIntl();

  return useMemo(() => {
    if (!lastActiveAt) {
      return {
        status: AVATAR_CONNECTION_STATUS.OFFLINE,
        message: intl.formatMessage(globalMessages.DISCONNECTED),
      };
    }

    const lastActiveSince = dayjs(lastActiveAt).fromNow();
    const diffInMinutes = dayjs().diff(dayjs(lastActiveAt), 'minute');

    const recentlyConnected = diffInMinutes < RECENTLY_CONNECTED_THRESHOLD_MINUTES;

    const status = recentlyConnected
      ? AVATAR_CONNECTION_STATUS.ONLINE
      : AVATAR_CONNECTION_STATUS.OFFLINE;

    const message = recentlyConnected
      ? intl.formatMessage(globalMessages.CONNECTED)
      : intl.formatMessage(globalMessages.ACTIVE_SINCE, { time: lastActiveSince });

    return {
      status,
      message,
      recentlyConnected,
    };
  }, [lastActiveAt, intl]);
};
